import moment from "moment";
import { Grouping, IUtility } from "../../nodeDetails/types";
import { DeviceType, Interval } from "../types";
import * as queryString from "common/query-string";

const buildUrl = (endpoint: string) => `${window.config.baseUrl}${endpoint}`;

// ** USER ** //
export const loginUrl = buildUrl("/token");
export const userInfoUrl = buildUrl(
  "/api/users/self?restrictDomainsToModule=mobileapp"
);

export const domainSettingsUrl = (domain: string) =>
  buildUrl(`/api/${domain}/settings`);

export const changePasswordUrl = buildUrl("/api/users/self/password");
export const verifyCodeUrl = buildUrl(`/api/users/self/verify/code`);
export const deletePhoneNumberUrl = (phoneNumber: string) =>
  buildUrl(
    `/api/users/self/phonenumbers?phonenumber=${encodeURIComponent(
      phoneNumber
    )}`
  );
export const verifyPhoneUrl = () =>
  buildUrl(`/api/users/self/verify/phonenumber`);
export const verifyEmailUrl = () => buildUrl(`/api/users/self/verify/email`);
export const resetPasswordUrl = buildUrl("/api/users/self/resetuserpassword");
// ** USER ** //

// ** COMMON ** //
export const searchUrl = (domain: string, searchTerm: string) =>
  buildUrl(
    `/api/${domain}/nodes?objectNumber=${searchTerm}&name=${searchTerm}&nodetypecode=9`
  );

export const getNodeWithParentsUrl = (domain: string, nodeId: number) =>
  buildUrl(`/api/${domain}/nodes/${nodeId}`);

export const measuringPointUrl = (domain: string, nodeId: number) =>
  buildUrl(
    `/api/${domain}/measuringpoints?nodeID=${nodeId}&includeSubNodes=true`
  );
export const nodeTreeUrl = (domain: string, nodeId: string) =>
  buildUrl(`/api/${domain}/nodes/${nodeId}?includesubnodes=true`);

export const measuringDeviceAlertUrl = (
  domain: string,
  measuringDeviceIdList: string[] | undefined
) =>
  buildUrl(
    `/api/${domain}/devicealerts?${measuringDeviceIdList
      ?.map((id) => `measuringDeviceID=${id}`)
      .reduce((acc, value) => `${acc}&${value}`, "")}`
  );

// ** COMMON ** //

export const currentTemperatureUrl = (domain: string, id: number) =>
  buildUrl(
    `/api/${domain}/data?nodeid=${id}&includeSubNodes=true&utl=T[avg]&from=${moment()
      .hour(0)
      .minute(0)
      .seconds(0)
      .unix()}&interval=H`
  );
export const currentRelativeHumidityUrl = (domain: string, id: number) =>
  buildUrl(
    `/api/${domain}/data?nodeid=${id}&includeSubNodes=true&utl=RH[avg]&from=${moment()
      .hour(0)
      .minute(0)
      .seconds(0)
      .unix()}&interval=H`
  );

// ** COMMENTS ** //
export const measuringpointCommentsUrl = (
  domain: string,
  measuringpointId: number
) => buildUrl(`/api/${domain}/measuringpoints/${measuringpointId}/Comments`);

export const deleteCommentUrl = (domain: string, commentId: number) =>
  buildUrl(`/api/${domain}/comments/${commentId}`);

export const putCommentUrl = (domain: string, id: number) =>
  buildUrl(`/api/${domain}/comments/${id}`);

export const addCommentUrl = (domain: string) =>
  buildUrl(`/api/${domain}/comments`);
export const nodeCommentsUrl = (domain: string, nodeId: number) =>
  buildUrl(`/api/${domain}/nodes/${nodeId}/comments?includeSubNodes=true`);
// ** COMMENTS ** //

// ** INSTALLATIONS ** //
export const installationsOnNodeUrl = (
  domain: string,
  nodeId: number,
  deviceTypes?: DeviceType | DeviceType[]
) =>
  buildUrl(
    `/api/${domain}/installations?nodeID=${nodeId}${
      deviceTypes ? `&${extractInstallations(deviceTypes)}` : ""
    }`
  );

export const installationsOnDomainUrl = (
  domain: string,
  deviceTypes?: DeviceType | DeviceType[]
) =>
  buildUrl(
    `/api/${domain}/installations?${
      deviceTypes ? extractInstallations(deviceTypes) : ""
    }`
  );
const extractInstallations = (deviceTypes: DeviceType | DeviceType[]) => {
  if (!Array.isArray(deviceTypes)) return `deviceType=${deviceTypes}`;
  let substring = "";
  for (let i = 0; i < deviceTypes.length; i++) {
    if (i === 0) {
      substring = `deviceType=${deviceTypes[i]}`;
      continue;
    }
    substring += `&deviceType=${deviceTypes[i]}`;
  }
  return substring;
};
// deviceType=${DeviceType.Smoke}&deviceType=${DeviceType.Leakage}
export const sensorsUrl = (domain: string, nodeId: string) =>
  buildUrl(`/api/${domain}/installations?nodeID=${nodeId}`);

// ** DATA ** //
export const dataUrl = (
  domain: string,
  nodeId: number,
  from: number,
  to: number,
  interval: Interval,
  utilities: IUtility[],
  grouping: Grouping
) =>
  buildUrl(
    `/api/${domain}/data?nodeID=${nodeId}&includeSubNodes=true&from=${from}&to=${to}&interval=${interval}&grouping=${grouping}${translateUtilsToString(
      utilities
    )}`
  );

export const meterEventsSubscriptionsUrl = (domain: string) =>
  buildUrl(`/api/${domain}/metereventssubsciptions`);

export const meterEventsUrl = (
  domain: string,
  nodeId: string,
  landlord?: boolean,
  from?: number,
  to?: number
) =>
  buildUrl(
    `/api/${domain}/meterevents?nodeID=${nodeId}${from ? `&from=${from}` : ""}${
      to ? `&to=${to}` : ""
    }${landlord ? "&eventtype=Sabotage&eventtype=Leakage&eventtype=Smoke" : ""}`
  );

const translateUtilsToString = (utilities: IUtility[]) => {
  let subString: string = "";
  utilities.length <= 1
    ? (subString = `&utl=${utilities[0].utlType}%5B${utilities[0].aggregate}%5D`)
    : utilities.forEach((util) => {
        subString = subString + `&utl=${util.utlType}%5B${util.aggregate}%5D`;
      });
  return subString;
};

// ** DATA ** //

// ** BILLING RESULTS ** //

export const billingResultsUrl = (
  domain: string,
  nodeID?: string | string[],
  objectNumber?: string | string[],
  contractCode?: string | string[],
  startFrom?: number,
  startTo?: number,
  resultID?: string | string[]
) => {
  const queryParamsString = queryString.stringify({
    nodeID,
    objectNumber,
    contractCode,
    startFrom,
    startTo,
    resultID,
  });

  return buildUrl(`/api/${domain}/billingresults${queryParamsString}`);
};

// ** BILLING RESULTS ** //
